import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  IconButton,
  Tooltip,
  Chip,
  Button,
  useTheme,
  useMediaQuery,
  Card,
  CardContent,
  Divider
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import RestoreIcon from '@mui/icons-material/Restore';
import AddIcon from '@mui/icons-material/Add';
import axios from '../../services/axiosConfig';
import { environment } from '../../config/environment';
import { ROLES, IRole } from '../../constants/roles';
import { IUser } from '../../types/user';
import UserEditDialog from './UserEditDialog';

const UserManagement: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const [users, setUsers] = useState<IUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<IUser | null>(null);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${environment.apiUrl}user`);
      console.log('Users response:', response.data);

      let usersData = Array.isArray(response.data) ? response.data : [];

      // Map roles to display roles
      usersData = usersData.map(user => {
        const displayRoles: IRole[] = [];
        for (const role of ROLES) {
          if (user.roles.indexOf(role.id) >= 0) {
            displayRoles.push(role);
          }
        }
        return { ...user, displayRoles };
      });

      setUsers(usersData);
    } catch (error) {
      console.error('Error loading users:', error);
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
  }, []);

  const handleDeleteUser = async (user: IUser) => {
    if (window.confirm('Are you sure you want to make this user INACTIVE?')) {
      try {
        await axios.delete(`${environment.apiUrl}user/${user.username}`);
        loadUsers();
      } catch (error) {
        console.error('Error deleting user:', error);
        alert('Network error. Try again later');
      }
    }
  };

  const handleRestoreUser = async (user: IUser) => {
    if (window.confirm('Are you sure you want to make this user ACTIVE?')) {
      try {
        await axios.patch(`${environment.apiUrl}user/${user.username}`);
        loadUsers();
      } catch (error) {
        console.error('Error restoring user:', error);
        alert('Network error. Try again later');
      }
    }
  };

  const handleEditUser = (user: IUser) => {
    setSelectedUser(user);
    setEditDialogOpen(true);
  };

  const handleNewUser = () => {
    setSelectedUser(null);
    setEditDialogOpen(true);
  };

  const handleDialogClose = () => {
    setEditDialogOpen(false);
    setSelectedUser(null);
  };

  const handleUserSaved = () => {
    loadUsers();
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3, md: 4 }, width: '100%' }}>
      <Box 
        display="flex" 
        justifyContent="space-between" 
        alignItems="center" 
        mb={3}
        flexWrap="wrap"
        gap={2}
      >
        <Typography variant="h5" sx={{ fontWeight: 500, color: 'text.secondary' }}>
          User Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          color="primary"
          onClick={handleNewUser}
          sx={{ minWidth: '140px' }}
        >
          Add User
        </Button>
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" p={6}>
          <CircularProgress size={48} />
        </Box>
      ) : (
        <>
          {/* Mobile Card View */}
          <Box sx={{ display: { xs: 'block', md: 'none' } }}>
            {users.map((user) => (
              <Card 
                key={user.username} 
                sx={{ 
                  mb: 2, 
                  border: user.isDeleted ? '1px solid' : 'none',
                  borderColor: user.isDeleted ? 'error.main' : 'transparent',
                  backgroundColor: user.isDeleted ? 'rgba(244, 67, 54, 0.08)' : 'background.paper'
                }}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: user.isDeleted ? 'text.primary' : 'inherit' }}>
                      {user.username}
                    </Typography>
                    <Box display="flex" gap={0.5}>
                      <IconButton size="small" color="primary" onClick={() => handleEditUser(user)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                      {!user.isDeleted ? (
                        <IconButton size="small" color="error" onClick={() => handleDeleteUser(user)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      ) : (
                        <IconButton size="small" color="success" onClick={() => handleRestoreUser(user)}>
                          <RestoreIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Box>
                  </Box>
                  <Divider sx={{ my: 1.5 }} />
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>Roles:</Typography>
                      <Box display="flex" gap={0.5} flexWrap="wrap">
                        {user.displayRoles?.map((role) => (
                          <Chip
                            key={role.id}
                            label={role.itemName}
                            size="small"
                            color={user.isDeleted ? 'default' : 'primary'}
                            variant="outlined"
                          />
                        ))}
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">Created On:</Typography>
                      <Typography variant="body2" fontWeight={500}>
                        {user.createdOn ? new Date(user.createdOn).toLocaleDateString() : '-'}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">Created By:</Typography>
                      <Typography variant="body2" fontWeight={500}>{user.createdBy}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">Updated On:</Typography>
                      <Typography variant="body2" fontWeight={500}>
                        {user.updatedOn ? new Date(user.updatedOn).toLocaleDateString() : '-'}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">Updated By:</Typography>
                      <Typography variant="body2" fontWeight={500}>{user.updatedBy}</Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Box>

          {/* Desktop Table View */}
          <Paper sx={{ borderRadius: 2, overflow: 'hidden', display: { xs: 'none', md: 'block' } }}>
            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table sx={{ minWidth: 650, tableLayout: 'auto' }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, minWidth: 120 }}>Username</TableCell>
                    <TableCell sx={{ fontWeight: 600, minWidth: 150 }}>Roles</TableCell>
                    <TableCell sx={{ fontWeight: 600, minWidth: 100 }}>Created On</TableCell>
                    <TableCell sx={{ fontWeight: 600, minWidth: 100 }}>Created By</TableCell>
                    <TableCell sx={{ fontWeight: 600, minWidth: 100 }}>Updated On</TableCell>
                    <TableCell sx={{ fontWeight: 600, minWidth: 100 }}>Updated By</TableCell>
                    <TableCell sx={{ fontWeight: 600, minWidth: 120 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
              <TableBody>
                {users.map((user) => (
                  <TableRow
                    key={user.username}
                    sx={{
                      backgroundColor: user.isDeleted ? 'rgba(244, 67, 54, 0.08)' : 'inherit',
                      '&:hover': {
                        backgroundColor: user.isDeleted ? 'rgba(244, 67, 54, 0.12)' : 'action.hover',
                      },
                    }}
                  >
                    <TableCell sx={{ color: user.isDeleted ? 'text.primary' : 'inherit', wordBreak: 'break-word' }}>
                      {user.username}
                    </TableCell>
                    <TableCell sx={{ color: user.isDeleted ? 'text.primary' : 'inherit' }}>
                      <Box display="flex" gap={0.5} flexWrap="wrap">
                        {user.displayRoles?.map((role) => (
                          <Chip
                            key={role.id}
                            label={role.itemName}
                            size="small"
                            color={user.isDeleted ? 'default' : 'primary'}
                            variant="outlined"
                          />
                        ))}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ color: user.isDeleted ? 'text.primary' : 'inherit' }}>
                      {user.createdOn ? new Date(user.createdOn).toLocaleDateString() : '-'}
                    </TableCell>
                    <TableCell sx={{ color: user.isDeleted ? 'text.primary' : 'inherit' }}>
                      {user.createdBy}
                    </TableCell>
                    <TableCell sx={{ color: user.isDeleted ? 'text.primary' : 'inherit' }}>
                      {user.updatedOn ? new Date(user.updatedOn).toLocaleDateString() : '-'}
                    </TableCell>
                    <TableCell sx={{ color: user.isDeleted ? 'text.primary' : 'inherit' }}>
                      {user.updatedBy}
                    </TableCell>
                    <TableCell>
                      <Box display="flex" gap={1}>
                        <Tooltip title="Edit">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleEditUser(user)}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>

                        {!user.isDeleted ? (
                          <Tooltip title="Make Inactive">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDeleteUser(user)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        ) : (
                          <Tooltip title="Make Active">
                            <IconButton
                              size="small"
                              color="success"
                              onClick={() => handleRestoreUser(user)}
                            >
                              <RestoreIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          </Paper>
        </>
      )}

      <UserEditDialog
        open={editDialogOpen}
        onClose={handleDialogClose}
        user={selectedUser}
        onSaved={handleUserSaved}
      />
    </Box>
  );
};

export default UserManagement;
